import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, from } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { mergeLists } from '@/lib/apollo/mergeLists'
import { keepaliveLink } from '@/lib/apollo/keepalive'
import { isBackendUnreachable } from '@/lib/auth/classifySessionError'
import {
  notifyBackendAvailable,
  notifyBackendUnavailable,
} from '@/lib/auth/backendAvailability'

const httpLink = createHttpLink({
  uri: `${import.meta.env.VITE_API_URL}/graphql`,
})

const authLink = setContext((_, { headers }) => {
  // Get the authentication token from local storage if it exists
  const token = localStorage.getItem('token')

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    }
  }
})

const errorLink = onError(({ networkError }) => {
  if (networkError && isBackendUnreachable(networkError)) {
    notifyBackendUnavailable()
  }
})

const availabilityLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((result) => {
    notifyBackendAvailable()
    return result
  })
})

export const client = new ApolloClient({
  link: from([errorLink, availabilityLink, keepaliveLink, authLink.concat(httpLink)]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          myProjects: {
            keyArgs: false,
            merge: mergeLists,
          },
          myLibraries: {
            keyArgs: false,
            merge: mergeLists,
          },
          communityLibraries: {
            keyArgs: false,
            merge: mergeLists,
          },
          myClips: {
            keyArgs: false,
            merge: mergeLists,
          },
          myAPIRequests: {
            keyArgs: false,
            merge: mergeLists,
          },
          myUserMedia: {
            keyArgs: false,
            merge: mergeLists,
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    },
    query: {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    },
  },
})

/** Evicts account-scoped data cached despite the client-wide `no-cache` policy.
 * Called on login, logout and session loss so a new account never sees the
 * previous account's values. */
export function resetAccountScopedCache(): void {
  client.cache.evict({ fieldName: 'mySocialPostingPlatforms' })
  client.cache.gc()
}

if (typeof window !== 'undefined') {
  // Same string as ACCOUNT_CHANGED_EVENT in axios.ts (not imported to keep the modules independent).
  window.addEventListener('auth:account-changed', resetAccountScopedCache)
}
